import React, { useMemo, useState } from "react";

import BoxLayer from "../retro/box-layer/box-layer.component";
import RetroButton from "../retro/button/retro-button.component";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCode,
  faCopy,
  faPlug,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

import "./station-api-generator.styles.scss";

const buildFirestoreRestUrl = (projectId, path) => {
  if (!projectId) return "";

  return `https://firestore.googleapis.com/v1/projects/${projectId}/databases/(default)/documents/${path}`;
};

const buildStationApi = ({ currentSpaceId, currentStationId, data }) => {
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || "";
  const stationPath = `space/${currentSpaceId}/stations/${currentStationId}`;
  const tasksPath = `${stationPath}/tasks/tasks`;
  const modulesPath = `${stationPath}/modules/modules`;
  const stationName = data?.name || "Current station";

  const connection = {
    stationName,
    spaceId: currentSpaceId,
    stationId: currentStationId,
    firestore: {
      stationPath,
      tasksPath,
      modulesPath,
    },
    rest: {
      station: buildFirestoreRestUrl(projectId, stationPath),
      tasks: buildFirestoreRestUrl(projectId, tasksPath),
      modules: buildFirestoreRestUrl(projectId, modulesPath),
    },
  };

  const code = `// ${stationName} API connection
const stationApi = ${JSON.stringify(connection, null, 2)};

// Firebase Web SDK
const tasksRef = db.doc(stationApi.firestore.tasksPath);
const modulesRef = db.doc(stationApi.firestore.modulesPath);

tasksRef.onSnapshot((doc) => {
  console.log("station tasks", doc.data());
});

modulesRef.onSnapshot((doc) => {
  console.log("station modules", doc.data());
});

// REST example
fetch(stationApi.rest.tasks, {
  headers: {
    Authorization: "Bearer <FIREBASE_ID_TOKEN>",
  },
})
  .then((res) => res.json())
  .then((tasks) => console.log(tasks));`;

  return { ...connection, code };
};

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

const StationApiGenerator = ({ currentSpaceId, currentStationId, data }) => {
  const [showApi, setShowApi] = useState(false);
  const [copied, setCopied] = useState(false);

  const api = useMemo(
    () => buildStationApi({ currentSpaceId, currentStationId, data }),
    [currentSpaceId, currentStationId, data]
  );

  const handleCopy = async () => {
    await copyText(api.code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="stationApi">
      <RetroButton
        mode="flat2"
        color="success"
        onClick={() => setShowApi(true)}
        disabled={!currentSpaceId || !currentStationId}
      >
        <FontAwesomeIcon icon={faPlug} />
        Generate API
      </RetroButton>

      {showApi && (
        <BoxLayer setLayer={setShowApi}>
          <div className="stationApi__panel">
            <div className="stationApi__header">
              <div>
                <p className="stationApi__eyebrow">Station API</p>
                <h2>{api.stationName}</h2>
              </div>
              <button
                className="stationApi__close"
                type="button"
                onClick={() => setShowApi(false)}
                aria-label="Close API panel"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="stationApi__summary">
              <div>
                <span>Space ID</span>
                <strong>{api.spaceId}</strong>
              </div>
              <div>
                <span>Station ID</span>
                <strong>{api.stationId}</strong>
              </div>
            </div>

            <div className="stationApi__paths">
              <div>
                <span>Tasks</span>
                <code>{api.firestore.tasksPath}</code>
              </div>
              <div>
                <span>Modules</span>
                <code>{api.firestore.modulesPath}</code>
              </div>
            </div>

            <div className="stationApi__codeHeader">
              <div>
                <FontAwesomeIcon icon={faCode} />
                <span>Connection snippet</span>
              </div>
              <button type="button" onClick={handleCopy}>
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            <pre className="stationApi__code">
              <code>{api.code}</code>
            </pre>
          </div>
        </BoxLayer>
      )}
    </div>
  );
};

export default StationApiGenerator;
